import type { CameraMode } from '../game/Camera';
import { attachMenuSounds } from './MenuSounds';
import { vibrate } from '../game/Haptics';

export interface CameraToggleHandlers {
  onToggle: (newMode: CameraMode) => void;
}

/**
 * Camera-mode swap button. Mirrors the in-race pause button — pointer
 * events drive the touch / mouse path, the click event is honoured only
 * for keyboard activation (filtered by MouseEvent.detail === 0). Earlier
 * iterations used a setTimeout-based guard to swallow the pointer-
 * synthesised click; on real devices that click occasionally arrived
 * after the timeout fired, which double-toggled the camera back to its
 * starting mode — the "tap doesn't change the view" bug.
 */
export class CameraToggle {
  readonly root: HTMLButtonElement;
  private currentMode: CameraMode;
  private armedPointerId: number | null = null;
  private readonly windowRelease: (e: Event) => void;

  constructor(parent: HTMLElement, initialMode: CameraMode, handlers: CameraToggleHandlers) {
    this.currentMode = initialMode;

    const root = document.createElement('button');
    root.id = 'camera-toggle';
    root.className = 'tst-btn tst-btn-sm';
    root.type = 'button';
    root.hidden = true;
    this.root = root;

    const fire = (): void => {
      const next: CameraMode = this.currentMode === 'topDown' ? 'chase' : 'topDown';
      this.setMode(next);
      handlers.onToggle(next);
    };

    const releasePress = (): void => {
      if (this.armedPointerId !== null) {
        try { root.releasePointerCapture(this.armedPointerId); } catch { /* ignore */ }
        this.armedPointerId = null;
      }
      root.classList.remove('is-pressed');
    };

    root.addEventListener('pointerdown', (e: PointerEvent) => {
      e.preventDefault();
      this.armedPointerId = e.pointerId;
      root.classList.add('is-pressed');
      try { root.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    });

    root.addEventListener('pointerup', (e: PointerEvent) => {
      if (this.armedPointerId !== e.pointerId) return;
      releasePress();
      vibrate(15);
      fire();
    });

    const cancel = (e: PointerEvent): void => {
      if (this.armedPointerId !== e.pointerId) return;
      releasePress();
    };
    root.addEventListener('pointercancel', cancel);
    root.addEventListener('lostpointercapture', cancel);

    root.addEventListener('click', (e: MouseEvent) => {
      // Pointer-driven clicks have detail >= 1; we already handled them
      // in pointerup. detail === 0 is a keyboard-driven activation
      // (Enter / Space on a focused button).
      if (e.detail !== 0) return;
      vibrate(15);
      fire();
    });

    // Safety net: if the OS reclaims the pointer (notification shade,
    // Android nav gesture, iOS edge-swipe) we may never see pointerup
    // or pointercancel on the element. A window-level listener drops
    // the press so the button can't get stuck in `is-pressed`.
    this.windowRelease = (e: Event): void => {
      const pe = e as PointerEvent;
      if (this.armedPointerId === null) return;
      if (this.armedPointerId !== pe.pointerId) return;
      releasePress();
    };
    window.addEventListener('pointerup', this.windowRelease);
    window.addEventListener('pointercancel', this.windowRelease);

    attachMenuSounds(root);

    this.refresh();
    parent.appendChild(root);
  }

  setMode(mode: CameraMode): void {
    this.currentMode = mode;
    this.refresh();
  }

  show(): void {
    this.root.hidden = false;
  }

  hide(): void {
    this.root.hidden = true;
  }

  private refresh(): void {
    const mode = this.currentMode === 'topDown' ? 'Tactical' : 'Chase';
    this.root.setAttribute('aria-label', `Camera ${mode}`);
    this.root.innerHTML = `
      <span class="camera-label">Camera</span>
      <span class="camera-mode">${mode}</span>
      <span class="camera-key" aria-hidden="true">C</span>
    `;
  }
}
